import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef
} from '@angular/core';
import { HistoricalChartComponent } from 'app/charts-dashboard/components/charts/historical-chart/historical-chart.component';
import { ChartsCommonService } from 'app/charts-dashboard/services/charts-common.service';
import { ChartsHttpService } from 'app/charts-dashboard/services/charts-http.service';
import {
  ChartsDashboardSeriesQuery,
  IChartSeries
} from 'app/charts-dashboard/state/series';
import {
  AxisXHistorical,
  Datetime,
  IChartFilter,
  IChartPointsResponse,
  ILCPoint,
  IMappedSeriesPointsResponse,
  IPointsData,
  ISpotChartParameters,
  Point,
  PointsResponseType,
  PointType,
  PointXType,
  PointXTypes
} from 'app/charts-dashboard/interfaces';
import { ChartsDashboardQuery } from 'app/charts-dashboard/state/dashboard';
import { ChartsDashboardCardQuery } from 'app/charts-dashboard/state/card';
import {
  ColorHEX,
  LineSeries,
  SeriesXY,
  SolidFill,
  SolidLine
} from '@arction/lcjs';
import { Observable } from 'rxjs';
import {
  debounceTime,
  map,
  takeUntil
} from 'rxjs/operators';
import { CommonService } from 'app/core/services/common.service';
import {
  EntityAction,
  EntityActions,
  ID
} from '@datorama/akita';
import { isIntraday } from 'app/charts-dashboard/helpers/charts-module-helpers';
import { ChartsDashboardStateService } from 'app/charts-dashboard/state/charts-dashboard-state.service';

@Component({
  selector: 'iv-live-spot-chart',
  templateUrl: '../common-chart/common-chart.component.html',
  styleUrls: ['../common-chart/common-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SpotChartComponent extends HistoricalChartComponent {

  constructor(
    protected readonly commonService: CommonService,
    protected readonly chartsCommonService: ChartsCommonService,
    protected readonly elementRef: ElementRef,
    protected readonly changeDetectorRef: ChangeDetectorRef,
    protected readonly dashboardQuery: ChartsDashboardQuery,
    protected readonly seriesQuery: ChartsDashboardSeriesQuery,
    protected readonly cardsQuery: ChartsDashboardCardQuery,
    protected readonly httpService: ChartsHttpService,
    protected readonly chartDashboardState: ChartsDashboardStateService
  ) {
    super(commonService, chartsCommonService, elementRef, changeDetectorRef, dashboardQuery, seriesQuery, cardsQuery, httpService, chartDashboardState);
  }

  private static mapVWapPoints(points: Point<AxisXHistorical>[]): Point<AxisXHistorical>[] {
    return points.map((point: Point<AxisXHistorical>) => {
      return {
        x: point.x,
        y: point.z
      };
    }).filter((point: Point<AxisXHistorical>) => point.y);
  }

  protected addSeriesVWap(): void {
    this.removeSeriesVWap();
    this.changeDetectorRef.detectChanges();

    this.card.chartSeries.forEach((id: ID) => {
      if (!(this.pointsData[id] as IChartPointsResponse<AxisXHistorical>)?.points) return;

      const pointsData: IMappedSeriesPointsResponse<AxisXHistorical> = {
        seriesId: id,
        ...this.pointsData[id] as IMappedSeriesPointsResponse<AxisXHistorical>,
        points: SpotChartComponent.mapVWapPoints((this.pointsData[id] as IChartPointsResponse<AxisXHistorical>).points) as Point<AxisXHistorical>[]
      };

      const series: IChartSeries = this.seriesQuery.getEntity(id);
      const lineSeries: LineSeries = this.chart
        .addLineSeries()
        .setStrokeStyle(new SolidLine(
          {
            thickness: 1,
            fillStyle: new SolidFill(
              {
                color: ColorHEX(series.settings.color)
              }
            )
          })
        )
        .add(this.mapPoints(pointsData))
        .setName(series.stock.symbol);

      this.lineSeries = {
        ...this.lineSeries,
        [`${id}-vwap`]: lineSeries,
      };

      this.setCursorResultTableFormatterVWap(this.lineSeries[`${id}-vwap`], series);
    });
  }

  private removeSeriesVWap(seriesId?: ID): void {
    if (seriesId) {
      this.disposeLineChart(`${seriesId}-vwap`);
      return;
    }

    this.card.chartSeries.forEach((id: ID) => {
      this.disposeLineChart(`${id}-vwap`);
    });
  }

  private setCursorResultTableFormatterVWap(lineSeries: LineSeries, series: IChartSeries): void {
    lineSeries
      .setCursorResultTableFormatter((builder, _series: SeriesXY, x: number, y: number, dataPoint: ILCPoint) => {
        this.builderCursorResult = builder;
        const dateTimeRow = this.getDateTimeRow(_series,x)
        this.builderCursorResult
          .addRow(`${this.cursorResult.x === 'Date' ? '' : this.cursorResult.x} ${dateTimeRow}`.replace(',', ''))
          .addRow(`${series.stock.symbol}`)
          .addRow(`VWAP: ${parseFloat(dataPoint.y.toFixed(4))}`);

        return this.builderCursorResult;
      });
  }

  protected setCursorResultTableFormatter(lineSeries: LineSeries, series: IChartSeries, filters?: IChartFilter[], doNotShowAxisZ?: boolean): void {
    if (!lineSeries) return;

    lineSeries
      .setCursorResultTableFormatter((builder, _series: SeriesXY, x: number, y: number, dataPoint: ILCPoint) => {
        this.builderCursorResult = builder;
        const dateTimeRow = this.getDateTimeRow(_series,x)
        this.builderCursorResult
          .addRow(`${this.cursorResult.x === 'Date' ? '' : this.cursorResult.x} ${dateTimeRow}`.replace(',', ''))
          .addRow(`${series.stock.symbol}`)
          .addRow(`${this.cursorResult.y}: ${isIntraday(this.mainSeries.selectedFilters.timeRange.name)
            ? parseFloat(dataPoint.y.toFixed(4))
            : Math.round((y + Number.EPSILON) * 100) / 100}`);

        return this.builderCursorResult;
      });
  }

  protected createSeriesObservables(seriesArr: IChartSeries[], isFirstLoad?: boolean): Observable<IMappedSeriesPointsResponse<AxisXHistorical>>[] {
    return seriesArr.map((series: IChartSeries) => {
      return this.httpService.getSpotChart(this.getChartsParams(series, isFirstLoad) as ISpotChartParameters)
        .pipe(map((data: IChartPointsResponse<AxisXHistorical>) => {
          return {
            ...data,
            seriesId: series.id
          };
        }));
    });
  }

  protected getChartsParams(series: IChartSeries, isFirstLoad?: boolean): ISpotChartParameters {
    if (!series) return;

    let from: Datetime;

    if (!isFirstLoad) {
      from = this.getFrom(series);
    }

    return {
      stockId: series.stock.stockId.toString(),
      symbol: series.stock.symbol,
      hasOptions: true,
      from,
      timeRange: series.selectedFilters.timeRange.name,
      sendUserMetrics: !this.isSentUserMetrics && this.mainSeries.id === series.id
    };
  }

  protected seriesSubscription() {
    super.seriesSubscription();
    this.seriesQuery.selectEntityAction([EntityActions.Add, EntityActions.Update, EntityActions.Remove])
      .pipe(
        debounceTime(500),
        takeUntil(this.destroy$)
      )
      .subscribe((action: EntityAction<ID>) => {
        switch (action.type) {
          case EntityActions.Add:
            if (action.ids.filter(id => this.card.chartSeries.includes(id)).length && this.mainSeries.selectedFilters.vModeToggler) {
              this.addSeriesVWap();
            }
            break;
          case EntityActions.Update:
            if (action.ids.filter(id => this.card.chartSeries.includes(id)).length) {
              if (!this.mainSeries.selectedFilters.vModeToggler) {
                this.removeSeriesVWap();
              }
            }
            break;
          case EntityActions.Remove:
            this.removeSeriesVWap(action.ids[0]);
            break;
        }
      });
  }

  protected addPointsFromRt(data: IMappedSeriesPointsResponse<AxisXHistorical>): void {
    super.addPointsFromRt(data);

    if (this.lineSeries[`${data.seriesId}-vwap`]) {
      this.lineSeries[`${data.seriesId}-vwap`]
        .add(
          this.mapPoints(
            {
              ...this.pointsData[data.seriesId],
              points: SpotChartComponent.mapVWapPoints(data.points)} as IMappedSeriesPointsResponse<AxisXHistorical>
          )
        );
    }
  }
}
